import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { marked } from 'marked';
import { action } from '@ember/object';

export default class MarkdownRendererComponent extends Component {
  @tracked renderedContent = '';

  constructor() {
    super(...arguments);

    this.setupMarked();

    this.renderMarkdown();
  }

  setupMarked() {
    // Configure marked with custom renderer for Tailwind classes
    const renderer = new marked.Renderer();

    // Override heading rendering to add Tailwind classes
    renderer.heading = (text, level) => {
      const classes = {
        1: 'text-2xl font-bold my-1',
        2: 'text-xl font-semibold my-1',
        3: 'text-lg font-semibold my-1',
        4: 'text-base font-semibold my-1',
        5: 'text-sm font-semibold my-1',
        6: 'text-xs font-semibold my-1',
      };
      return `<h${level} class="${classes[level] || ''}">${text}</h${level}>`;
    };

    // Override paragraph rendering
    renderer.paragraph = (text) => {
      return `<p class="mb-2 text-sm leading-relaxed">${text}</p>`;
    };

    // Override link rendering
    renderer.link = (href, title, text) => {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}"${titleAttr} class="text-primary-600 hover:text-primary-800 underline" target="_blank" rel="noopener noreferrer">${text}</a>`;
    };

    // Override list rendering
    renderer.list = (body, ordered) => {
      const tag = ordered ? 'ol' : 'ul';
      const classes = ordered
        ? 'list-inside mb-4 pl-4'
        : 'list-disc list-inside mb-4 pl-4 text-sm';
      return `<${tag} class="${classes}">\n${body}</${tag}>\n`;
    };

    renderer.listitem = (text) => {
      return `<li class="mb-1 text-sm">${text}</li>\n`;
    };

    // Override blockquote rendering
    renderer.blockquote = (quote) => {
      return `<blockquote class="border-l-4 text-sm border-gray-300 pl-4 italic text-gray-600 my-4">${quote}</blockquote>`;
    };

    // Override table rendering
    renderer.table = (header, body) => {
      return `<table class="min-w-full divide-y divide-gray-200 my-4 text-sm">
        <thead class="bg-gray-50">${header}</thead>
        <tbody class="bg-white divide-y divide-gray-200">${body}</tbody>
      </table>`;
    };

    renderer.tablerow = (content) => {
      return `<tr>${content}</tr>\n`;
    };

    renderer.tablecell = (content, flags) => {
      const tag = flags.header ? 'th' : 'td';
      const classes = flags.header
        ? 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
        : 'px-6 py-4 whitespace-nowrap text-sm text-gray-900';
      return `<${tag} class="${classes}">${content}</${tag}>\n`;
    };

    // Set marked options
    marked.setOptions({
      renderer: renderer,
      breaks: true, // Convert \n to <br>
      gfm: true, // GitHub Flavored Markdown
      pedantic: false,
      sanitize: false, // Set to true for user-generated content
      smartypants: false,
    });
  }

  @action
  renderMarkdown() {
    const content = this.args.content || '';

    if (!content.trim()) {
      this.renderedContent = htmlSafe('');
      return;
    }

    try {
      const html = marked.parse(content);
      this.renderedContent = htmlSafe(html);
    } catch (error) {
      console.error('Error rendering markdown:', error);
      this.renderedContent = htmlSafe(
        `<p class="text-red-500">Error rendering markdown</p>`,
      );
    }
  }
}
